import { useMemo } from 'react';
import { z } from 'zod';
import { Action, Subject } from '@erp/authz';
import {
  reportFiltersSchema,
  WORKBOOK_PREVIEW_ROWS,
  workbookDefinitionSchema,
  workbookSampleSchema,
  workbookSourceSchema,
} from '@erp/shared-types';
import { definitionWithFormulaText } from '@erp/workbooks';
import { rpcClient } from '@/api/rpc/client';
import { useCan } from '@/hooks/useCan';
import { useFrontendTool } from '@/hooks/useFrontendTool';
import { buildComponentCatalog } from '@/components/report-canvas/canvasGeneration';
import type { WorkbookAgentPort } from './useWorkbookAgentTools';
import { workbookReportRegistry } from './workbookReportRegistry';
import {
  agentRunSummaries,
  agentStatefulTables,
  agentWorksheetResult,
} from './workbookAgentReads';
import { workbookAgentOperationSchema } from './workbookAgentEdits';
import { previewAgentChange } from './workbookAgentPreview';

/** Runs workbook_runs lists unless asked for more: the newest are what matter. */
const AGENT_RUNS = 5;
/** Rows of a Result read_worksheet_result returns unless asked for more. */
const AGENT_RESULT_ROWS = 20;

/** The on-page agent's reads: the Workbook as edited, its data, Runs, Results and Automations. */
export function useWorkbookAgentReadTools(
  workbookId: string,
  port: WorkbookAgentPort,
) {
  const [canReadTasks] = useCan(Action.Read, Subject.Task);
  const [canExecute] = useCan(Action.Execute, Subject.Workbook);
  const getTool = useFrontendTool({
    name: 'get_workbook',
    description:
      'Read the workbook as the user sees it now, unsaved edits included: its definition (column formulas as Excel text, e.g. "=[@Cases] * [@[Case Price]]"), editVersion, and its diagnostics (problems that block a run, written for users). includeSchema adds the definition schema.',
    parameters: z.object({ includeSchema: z.boolean().default(false) }),
    readOnlyHint: true,
    awaitable: true,
    execute: (input) => {
      const { definition, token, revision } = port.snapshot();
      return {
        ...(input.includeSchema
          ? {
              definitionSchema: z.toJSONSchema(workbookDefinitionSchema, {
                io: 'input',
              }),
            }
          : {}),
        definition: definitionWithFormulaText(definition, {
          columns: port.plan(definition).tables,
        }),
        editVersion: token,
        revision,
        diagnostics: port.diagnostics(),
      };
    },
  });
  const inspectTool = useFrontendTool({
    name: 'inspect_workbook_file',
    description:
      'Inspect an attached .xlsx file: sheets, 30 rows of cells, cached values, formulas, and number formats. Repeat with startRow to inspect another range.',
    parameters: z.object({
      fileId: z.uuid(),
      sheetName: z.string().optional(),
      startRow: z.number().int().positive().default(1),
    }),
    readOnlyHint: true,
    awaitable: true,
    execute: (input) => rpcClient.workbooks.inspect({ workbookId, ...input }),
  });
  const catalogTool = useFrontendTool({
    name: 'workbook_catalog',
    description:
      "List the data the user can add as a source (sources): SQL Data Source links (with source and column labels and descriptions; column name is the raw identifier to use in queries; schemaId/schemaName are the Schema Definition a link belongs to; a schema-group source combines a schema's data sources, all of them by default), Data Mappings, Data Assets (with their versions) and Entities (with their columns; an Entity Source's id is its entity). Also the Stateful tables a sink output can write (statefulTables): id, name, description, columns with their types, and keyColumns.",
    parameters: z.object({}),
    readOnlyHint: true,
    awaitable: true,
    execute: async () => {
      const [sources, targets] = await Promise.all([
        rpcClient.workbooks.catalog({}),
        rpcClient.workbooks.sinkTargets({}).then(
          (found) => ({ ok: true as const, found }),
          (error: unknown) => ({ ok: false as const, error }),
        ),
      ]);
      // Sources stay usable when sink targets can't be listed.
      return targets.ok
        ? { sources, statefulTables: agentStatefulTables(targets.found) }
        : {
            sources,
            statefulTablesUnavailable:
              targets.error instanceof Error
                ? targets.error.message
                : 'Stateful tables could not be listed.',
          };
    },
  });
  const objectCatalogTool = useFrontendTool({
    name: 'workbook_object_catalog',
    description:
      'Discover the Gently query-builder subject areas, fields and relationships available for object inputs.',
    parameters: z.object({}),
    readOnlyHint: true,
    awaitable: true,
    execute: () => rpcClient.procurement.queryBuilder.catalog({}),
  });
  const sourceTool = useFrontendTool({
    name: 'preview_workbook_source',
    description:
      'Inspect source columns and at most 100 sample rows. These rows are a preview, not a full export.',
    parameters: z.object({
      sourceJson: z
        .string()
        .max(32 * 1024 * 1024)
        .describe(
          'JSON source object matching a source in the workbook definition schema.',
        ),
    }),
    readOnlyHint: true,
    awaitable: true,
    execute: (input) =>
      rpcClient.workbooks.source({
        workbookId,
        source: workbookSourceSchema.parse(JSON.parse(input.sourceJson)),
      }),
  });
  const runsTool = useFrontendTool({
    name: 'workbook_runs',
    description:
      "Read the newest runs, newest first: status, mode, target, who started it, and each worksheet's row count, error count, failing validations and whether its result is still current for the edited workbook. No rows: read them with read_worksheet_result. A failed run's error is written for users (it names the column, the source and the first value); quote it rather than rephrasing it in SQL terms. mode 'sample' is a run on a sample (its sample: key values taken of all, and how far it drifts from the full data). drift lists large shifts from the previous comparable run, or for a full run from the sample before it (row counts, lookup match rates, value splits, totals): warnings to explain, never failures.",
    parameters: z.object({
      limit: z.number().int().min(1).max(50).default(AGENT_RUNS),
    }),
    readOnlyHint: true,
    awaitable: true,
    execute: async (input) =>
      agentRunSummaries(
        await rpcClient.workbooks.runs({ workbookId }),
        port.snapshot().definition,
        input.limit,
      ),
  });
  const resultTool = useFrontendTool({
    name: 'read_worksheet_result',
    description:
      "Read one worksheet's result as its tab shows it (its newest current result, else its newest): columns, first rows, row count, error count, the rows each filter removed, and each validation's outcome with a few failing rows. current false means the worksheet changed since that run. Pass sample to read a large result's first, last, random, stratified or most recent rows instead of its first rows.",
    parameters: z.object({
      sheetId: z.uuid(),
      rowLimit: z
        .number()
        .int()
        .min(0)
        .max(WORKBOOK_PREVIEW_ROWS)
        .default(AGENT_RESULT_ROWS),
      sample: workbookSampleSchema.optional(),
    }),
    readOnlyHint: true,
    awaitable: true,
    execute: async (input) => {
      const read = agentWorksheetResult(
        await rpcClient.workbooks.runs({ workbookId }),
        port.snapshot().definition,
        input.sheetId,
        input.rowLimit,
      );
      if (!read)
        return {
          ok: false,
          error:
            'No run has calculated this worksheet yet. Run through it first.',
        };
      if (!input.sample) return read;
      const sampled = await rpcClient.workbooks.resultSample({
        workbookId,
        runId: read.runId,
        sheetId: input.sheetId,
        sample: input.sample,
      });
      return {
        ...read,
        rows: sampled.rows.slice(0, input.rowLimit),
        sampledRowNumbers: sampled.rowNumbers.slice(0, input.rowLimit),
      };
    },
  });
  const previewTool = useFrontendTool({
    name: 'preview_workbook_change',
    enabled: canExecute,
    description:
      "Try a change before saving or running it: applies operations (edit_workbook's, in order, all or nothing) to the latest workbook in memory, without committing, saving or running anything, and calculates the target worksheet and everything downstream of it on the last run's inputs (inputsAsOf says when they were read; freshSources names the sources read now instead, and when). Returns the edited workbook's diagnostics and, per calculated worksheet, its columns with their declared types, first rows (at most 100), row count, error count with a few error cells and their causes, the rows each filter removed, and each validation's outcome. Pass no operations to preview the workbook as it is now. Refused with the diagnostics that block it, or when no earlier run holds this worksheet's inputs (then run up to it first). To keep the change, make it with edit_workbook.",
    parameters: z.object({
      sheet: z
        .string()
        .min(1)
        .describe("The target worksheet's id, or its name."),
      operations: z
        .array(workbookAgentOperationSchema)
        .max(50)
        .default([])
        .describe('The edit to try, as edit_workbook takes it.'),
      rowLimit: z
        .number()
        .int()
        .min(0)
        .max(WORKBOOK_PREVIEW_ROWS)
        .default(AGENT_RESULT_ROWS),
    }),
    readOnlyHint: true,
    awaitable: true,
    execute: (input) => previewAgentChange(workbookId, port, input),
  });
  const automationsTool = useFrontendTool({
    name: 'workbook_automations',
    description:
      "Read what automates this workbook: workflows that run it (Run Workbook node) or react to its runs (Workbook Run Completed trigger), agents it is attached to as a tool, and the tasks its task rules and instructions opened. Task rules themselves are in the definition's taskRules.",
    parameters: z.object({}),
    readOnlyHint: true,
    awaitable: true,
    execute: async () => ({
      ...(await rpcClient.workbooks.automations({ workbookId })),
      tasks: canReadTasks
        ? await rpcClient.workbooks.tasks({ workbookId })
        : 'You cannot read tasks.',
    }),
  });
  const downloadTool = useFrontendTool({
    name: 'workbook_output_link',
    description:
      "Get a short-lived download link for a file output (xlsx, or csv for a one-tab file) of the saved workbook, built from each tab's newest current result. Refused while a tab has no current result or failed an error validation. Reports are viewed and exported as PDF on their tab (open_workbook_item).",
    parameters: z.object({
      outputId: z.uuid(),
      format: z.enum(['xlsx', 'csv']).default('xlsx'),
    }),
    readOnlyHint: true,
    awaitable: true,
    execute: (input) => rpcClient.workbooks.download({ workbookId, ...input }),
  });
  const reportCatalogTool = useFrontendTool({
    name: 'report_component_catalog',
    description:
      "Read what a report can hold before laying one out: each component type (KPI, the builtin charts, Table, Custom chart 'vega-chart', Text) as an example component with its default fields, and its size on the 12-column grid; the report filters schema; and the organization's color palettes (paletteId; the default is used when none is set). A component's fields sit directly on it beside type and id, exactly as in its example (no config wrapper); a field its type doesn't have is refused.",
    parameters: z.object({}),
    readOnlyHint: true,
    awaitable: true,
    execute: async () => ({
      // As a component is written: its fields beside its type, not nested.
      components: buildComponentCatalog(workbookReportRegistry).map(
        ({ defaultConfig, ...entry }) => ({
          ...entry,
          example: { type: entry.type, id: 'unique-id', ...defaultConfig },
        }),
      ),
      filtersSchema: z.toJSONSchema(reportFiltersSchema, { io: 'input' }),
      palettes: (await rpcClient.reportColorPalettes.list({})).map(
        (palette) => ({
          id: palette.id,
          name: palette.name,
          isDefault: palette.isDefault,
        }),
      ),
    }),
  });
  return useMemo(
    () => [
      reportCatalogTool,
      getTool,
      inspectTool,
      catalogTool,
      sourceTool,
      objectCatalogTool,
      runsTool,
      resultTool,
      previewTool,
      automationsTool,
      downloadTool,
    ],
    [
      reportCatalogTool,
      getTool,
      inspectTool,
      catalogTool,
      sourceTool,
      objectCatalogTool,
      runsTool,
      resultTool,
      previewTool,
      automationsTool,
      downloadTool,
    ],
  );
}
